import { JsonRpcProvider, Web3Provider } from "@ethersproject/providers";
import { CoreProvider } from "@spectral-finance/react-core";
import React, { createContext, useContext, useState } from "react";
import { SpectralDialog } from "../components/SpectralDialog";
import "../styles/variables.css";
import { DialogStepsProvider } from "./DialogStepsContext";

// Onboard deps
import coinbaseWalletModule from "@web3-onboard/coinbase";
import gnosisModule from "@web3-onboard/gnosis";
import magicModule from "@web3-onboard/magic";
import portisModule from "@web3-onboard/portis";
import walletConnectModule from "@web3-onboard/walletconnect";
import walletLinkModule from "@web3-onboard/walletlink";

// Onboard config
const gnosis = gnosisModule();
const portis = portisModule({ apiKey: "813b1b13-7b65-45ab-a4ed-e1350a0ec2eb" });
const walletConnect = walletConnectModule();
const coinbaseWalletSdk = coinbaseWalletModule();
const walletLink = walletLinkModule();

// Magic
const magic = magicModule({
  apiKey: "pk_live_2954BFCDC2B75C31",
  userEmail:
    localStorage.getItem("magicUserEmail")?.match(/.+@.+/)?.[0] || undefined,
});
// const saveMagicEmail = ({ instance }: any) =>
//   (async () => {
//     const { email } = await instance.user.getMetadata();
//     localStorage.setItem("magicUserEmail", email);
//   })().catch(() => {});

const supportedWallets = [
  gnosis,
  portis,
  walletConnect,
  coinbaseWalletSdk,
  walletLink,
  magic,
];

// This should be based on .ENV
const VARIABLES = {
  alchemy: `https://eth-mainnet.g.alchemy.com/v2/6oTZOLvBDLNQM3_6ZIy-l_7h5n5xdb4D`,
  infura: `https://mainnet.infura.io/v3/315346d7de824f2da8986c159afdb4d6`,
  chainid: "421613",
  contract: "0x91e1156d5Fba6b1B251f396e96aFAaCE91394283",
  backendurl: "https://sdk.spectral.finance/api/v1",
};

type ISpectralContext = {
  start: () => void;
  close: () => void;
  score: number | null;
};

const SpectralContext = createContext({} as ISpectralContext);

const SpectralProvider: React.FC<{
  logo: string;
  partnerId: string;
  children: React.ReactNode;
}> = ({ logo, partnerId, children }) => {
  const [startedDialog, setStartedDialog] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [score, setScore] = useState(0);

  const ALCHEMY_PROVIDER = VARIABLES.alchemy;
  const providersLoader = {
    mainnetProvider: () =>
      new JsonRpcProvider(ALCHEMY_PROVIDER) as Web3Provider,
    altMainnetProvider: () =>
      new JsonRpcProvider(VARIABLES.infura) as Web3Provider,
  };

  const start = () => {
    setStartedDialog(true);
    setTimeout(() => setOpenDialog(true), 0);
  };

  const close = () => {
    setOpenDialog(false);
  };

  const onScoreComputed = (score: number) => {
    console.log("Score calculated and returned to partner", score);
    setScore(score);
  };

  if (!partnerId || !logo) {
    console.error(
      "Please verify that you set up `partnerId` and `logo` keys, otherwise the process will fail."
    );
  }

  return (
    <SpectralContext.Provider value={{ start, close, score }}>
      {children}
      {startedDialog && (
        <CoreProvider
          config={{
            chainId: VARIABLES.chainid,
            nfcContract: VARIABLES.contract,
            providersLoader,
            supportedWallets,
            backendUrl: VARIABLES.backendurl,
            partner: partnerId,
          }}
        >
          <DialogStepsProvider>
            <SpectralDialog
              open={openDialog}
              partnerLogo={logo}
              onScoreComputed={onScoreComputed}
            />
          </DialogStepsProvider>
        </CoreProvider>
      )}
    </SpectralContext.Provider>
  );
};

export const useSpectral = () => useContext(SpectralContext);
export { SpectralProvider };
